import 'angular';
import './approval_resource_selector.html';
import {findIndex} from 'lodash-es';
import {RootScope} from "../../../../types/angular-types";
import {ApprovalResource} from '../../../approvals-types';
import {IApprovalUtils} from '../../../ApprovalUtils';
import {IAuthenticatedUserService} from "../../../../types/ServiceTypes";
import SignalProvider from "../../../../gorillaboy/SignalProvider";


interface ResourceCollection {
    folders: ApprovalResource[];
    files: ApprovalResource[];
    marker: string;
}

interface IScope extends RootScope {
    // methods
    getParentResource: () => ApprovalResource;
    loadMore: () => void;
    isResourceSelected: (resource: ApprovalResource) => boolean;
    updateSelectedResource: (resource: ApprovalResource) => void;
    // properties
    approvalResources: ApprovalResource[];
    currentResources: ResourceCollection;
    selectedResource: ApprovalResource;
    resourcePath: ApprovalResource[];
    isLoading: boolean;
    loadMorePromise: ng.IPromise<ResourceCollection>;
}

const ROOT_RESOURCE_COLLECTION_URI = "rootResourcesCollection";

angular.module('GorillaBoy.Approvals')
.directive('approvalResourceSelector', ['$rootScope', '$q', '$log', 'ApprovalUtils', 'authenticatedUser', 'FileManagerFactory', ($rootScope: RootScope, $q: ng.IQService, $log: ng.ILogService, ApprovalUtils: IApprovalUtils, authenticatedUser: IAuthenticatedUserService, FileManagerFactory) => {
    return {
        restrict: 'E',
        scope: {
            approvalResources: '=',
            selectedResource: '='
        },
        templateUrl: './approvals/approvalView/approvalFilesPanel/approvalResourceSelector/approval_resource_selector.html',
        link: function($scope: IScope, $element, attrs) {
            // INITIALIZATION
            $scope.loadMorePromise = null;
            $scope.isLoading = false;
            FileManagerFactory.setUserContext(authenticatedUser.getUserInfo());
            $scope.fileManager = new FileManagerFactory.FileManager();

            initCurrentResources($scope.approvalResources);
            
            // METHODS
            $scope.updateCurrentResourcesByResource = (resource: ApprovalResource) => {
                if (resource.ResourceUri === ROOT_RESOURCE_COLLECTION_URI) {
                    initCurrentResources($scope.approvalResources);
                } else {
                    getResourceCollectionByResource(resource).then((newResourceCollection) => {
                        updateCurrentResources(newResourceCollection);
                        updateResourcePath(resource);
                    });
                }                
            };

            $scope.isResourceSelected = (resource: ApprovalResource) => {
                return $scope.selectedResource.ResourceUri === resource.ResourceUri;
            };

            $scope.updateSelectedResource = (resource: ApprovalResource) => {
                $scope.selectedResource = resource;
            }

            $scope.getParentResource = () => {
                return $scope.resourcePath[$scope.resourcePath.length - 2];
            }

            $scope.loadMore = () => {
                if ($scope.loadMorePromise !== null || !$scope.fileManager.getMarker()) {
                    return $scope.loadMorePromise;
                }

                $rootScope.$broadcast(SignalProvider.ContentLoading.Start);
                $scope.loadMorePromise = $scope.fileManager.loadMore()
                .then(() => {
                    return getResourceCollectionFromFileManager();
                }, (error) => {
                    return $q.reject(error);
                })
                .then((newResourceCollection) => {
                    updateCurrentResources(newResourceCollection);
                });
                $scope.loadMorePromise.finally(function() {
                    $rootScope.$broadcast(SignalProvider.ContentLoading.Done);
                    $scope.loadMorePromise = null;
                });
            };

            // LISTENERS
            $scope.$on("$destroy", function() {
                $scope.fileManager.destroy();
            });

            $scope.$on(SignalProvider.ContentLoading.LoadMore, function($event, data) {
                $scope.loadMore();
                $event.stopPropagation();
            });

            // HELPER FUNCTIONS
            function updateCurrentResources(resources: ResourceCollection) {
                $scope.currentResources = resources;
            }

            function updateResourcePath(resource: ApprovalResource) {
                let givenResourceIndex = findIndex($scope.resourcePath, (res: ApprovalResource) => res.ResourceUri === resource.ResourceUri);
                if (givenResourceIndex === -1) {
                    // assume it navigate into a new folder if resource is not found
                    $scope.resourcePath.push(resource)
                } else {
                    // remove the resources after the given resource
                    $scope.resourcePath.length = givenResourceIndex + 1;
                }
            }

            function initCurrentResources(resources: ApprovalResource[]) {
                $scope.resourcePath = [{
                    ResourceKind: null,
                    ResourceUri: ROOT_RESOURCE_COLLECTION_URI,
                    ResourceName: 'Approval resources',
                    Properties: []
                }];
                $scope.currentResources = {
                    folders: resources.filter((resource: ApprovalResource) => ApprovalUtils.isWorkDocsFolder(resource.ResourceUri)),
                    files: resources.filter((resource: ApprovalResource) => ApprovalUtils.isWorkDocsFile(resource.ResourceUri)),
                    marker: null
                }
            }

            function getResourceCollectionByResource (resource: ApprovalResource) {
                if (!ApprovalUtils.isWorkDocsFolder(resource.ResourceUri)) {
                    $log.error('cannot loading resource collection for ResourceUri: ' +  resource.ResourceUri);
                    return $q.reject();
                }

                let folderId = ApprovalUtils.getFolderIdFromResourceUri(resource.ResourceUri);

                return $q((resolve, reject)=> {
                    $scope.isLoading = true;
                    $scope.fileManager.setCurrentFolder(folderId);
                    let loadFolderPromise = $scope.fileManager.loadCurrentFolder().then((response) => {
                        resolve(getResourceCollectionFromFileManager());
                    }, (error) => {
                        reject(error);
                    });
                    loadFolderPromise.finally(() => {
                        $scope.isLoading = false;
                    });

                    return loadFolderPromise;
                });
            }

            function getResourceCollectionFromFileManager() {
                let result: ResourceCollection = {
                    folders: $scope.fileManager.getFolders().map(ApprovalUtils.folderMetadata2ApprovalResource),
                    files: $scope.fileManager.getFiles().map(ApprovalUtils.fileMetadata2ApprovalResource),
                    marker: $scope.fileManager.getMarker()? $scope.fileManager.getMarker(): null
                }
                return result;
            }
        }
    };
}]);